<script>
  /* global Global, DEV_ENV */
  import Header from '../../common/Header.svelte';
  import Footer from '../../common/Footer.svelte';
  import Loading from '../../common/Loading.svelte';
  import Error from '../../common/Error.svelte';
  import Title from '../../common/Title.svelte';
  import Input from '../../common/Input.svelte';
  import Button from '../../common/Button.svelte';
  import MessageBox from '../../common/MessageBox.svelte';
  import Common from '../../js/common.js';
  import Validation from '../../js/validation.js';
  import ALL_GAME_FIELDS from '../../js/fields/allGameFields.js';

  export let params;

  let lang;

  let user = JSON.parse(window.localStorage.getItem('user'));
  if (DEV_ENV) user = { firstName: 'Jan', lastName: 'Novák', roles: ['ROLE_ADMIN'] };
  if (!user.roles.includes('ROLE_ADMIN')) window.location.href = window.localStorage.getItem('previousPage') || './';
  const selectedYear = Number(params?.year) || Global.years[0];

  let game;

  let loading = true;
  let error = false;

  (async () => {
    // eslint-disable-next-line no-undef
    const response = await fetch(`${Common.BACKEND_URL}/admin/games/get/${params.game}/${selectedYear}`);

    if (response.ok) {
      const data = await response.json();

      game = data.game;
      user = data.user;

      if (user) {
        window.localStorage.setItem('user', JSON.stringify(data.user));
      } else {
        window.localStorage.removeItem('user');

        back();
      }

      Object.keys(game).forEach(key => {
        if (game[key] === null) {
          game[key] = undefined;
        } else if (typeof game[key] === 'object') {
          Object.keys(game[key]).forEach(nestedKey => {
            if (game[key][nestedKey] === null) game[key][nestedKey] = undefined;
          });
        }
      });
    } else {
      if (response.status === Common.NOT_FOUND || response.status === Common.UNAUTHORIZED) window.location.href = './';
      else error = true;
    }

    loading = false;
  })();

  let messageBox;
  let display = 'button';
  let errors = [];

  function editGame (validate = true) {
    display = 'loading';
    errors = [];

    messageBox = {
      cs: { color: 'blue', message: i18n.savingInProgress.cs },
      en: { color: 'blue', message: i18n.savingInProgress.en },
    };

    try {
      // eslint-disable-next-line no-undef
      const formData = new FormData();

      const data = Validation.convertAndCheck(ALL_GAME_FIELDS, validate, game);

      formData.append('data', JSON.stringify(data));

      (async () => {
        // eslint-disable-next-line no-undef
        const response = await fetch(`${Common.BACKEND_URL}/admin/games/edit/${params.game}/${selectedYear}`, {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          const data = await response.text();

          messageBox = {
            cs: { color: 'green', message: `Úpravy zápasu "${Common.addZeros(data)}" byly úspěšně uloženy.` },
            en: { color: 'green', message: `Game "${Common.addZeros(data)}" changes have been successfully saved.` },
          };

          display = 'saved';
        } else {
          messageBox = {
            cs: { color: 'red', message: i18n.unexpectedError.cs },
            en: { color: 'red', message: i18n.unexpectedError.en },
          };

          display = 'error';
        }
      })();
    } catch (error) {
      errors = error.errors;

      messageBox = {
        cs: { color: 'red', errors: errors.map(error => error.message.cs) },
        en: { color: 'red', errors: errors.map(error => error.message.en) },
      };

      display = 'error';
    }
  }

  function back () {
    window.location.href = window.localStorage.getItem('previousPage') || './';
  }

  async function translate (key) {
    // eslint-disable-next-line no-undef
    const response = await fetch(`${Common.BACKEND_URL}/translate`, {
      method: 'POST',
      body: game[key].cs,
    });

    if (response.ok) {
      game[key].en = await response.text();
    }
  }

  const i18n = {
    pageTitle: {
      cs: `Admin | ${Global.project.name.cs}`,
      en: `Admin | ${Global.project.name.en}`,
    },
    year: {
      cs: 'Ročník',
      en: 'Year',
    },
    number: {
      cs: 'Číslo',
      en: 'Number',
    },
    date: {
      cs: 'Datum',
      en: 'Date',
    },
    time: {
      cs: 'Čas',
      en: 'Time',
    },
    group: {
      cs: 'Skupina',
      en: 'Group',
    },
    visitor: {
      cs: 'Hosté',
      en: 'Visitor',
    },
    home: {
      cs: 'Domácí',
      en: 'Home',
    },
    field: {
      cs: 'Hřiště',
      en: 'Field',
    },
    czechTelevision: {
      cs: 'Česká televize',
      en: 'Czech Television',
    },
    photogallery: {
      cs: 'Fotogalerie',
      en: 'Photogallery',
    },
    scoreVisitor: {
      cs: 'Skóre hosté',
      en: 'Score visitor',
    },
    scoreHome: {
      cs: 'Skóre domácí',
      en: 'Score home',
    },
    note: {
      cs: 'Poznámka',
      en: 'Note',
    },
    automaticallyTranslate: {
      cs: 'Automaticky přeložit',
      en: 'Automatically translate',
    },
    translateInProgress: {
      cs: 'Probíhá překlad...',
      en: 'Translate in progress...',
    },
    saveChanges: {
      cs: 'Uložit úpravy',
      en: 'Save changes',
    },
    back: {
      cs: 'Zpět',
      en: 'Back',
    },
    savingInProgress: {
      cs: 'Probíhá ukládání úprav...',
      en: 'Changes saving in progress...',
    },
    unexpectedError: {
      cs: '(Ne)očekávaná chyba. `E29`',
      en: '(Un)expected error. `E29`',
    },
    withoutValidation: {
      cs: 'bez validace',
      en: 'without validation',
    },
  };

  const years = Global.years;
</script>

<svelte:head>
  <title> {i18n.pageTitle[lang]} </title>
</svelte:head>

<Header page='admin/games/{params.game}{selectedYear !== years[0] ? `/${selectedYear}` : ''}' bind:lang bind:user/>

<Title/>

<div class='admin-games-game'>
  <Loading bind:display={loading}/>
  <Error bind:display={error} bind:lang/>

  {#if !loading && !error}
    <div class='admin-games-game-box'>
      <div class='admin-games-game-box-group'>
        <!-- YEAR -->
        <Input bind:value={game.year} label={i18n.year[lang]} disabled={true}/>

        <!-- NUMBER-->
        <Input bind:value={game.number} label={i18n.number[lang]} disabled={true}/>
      </div>

      <!-- WBSC ID-->
      <Input bind:value={game.wbscId} label='WBSC ID' disabled={true}/>

      <div class='admin-games-game-box-group'>
        <!-- DATE -->
        <Input bind:value={game.date} label={i18n.date[lang]} disabled={true}/>

        <!-- TIME -->
        <Input bind:value={game.time} label={i18n.time[lang]} disabled={true}/>
      </div>

      <!-- GROUP -->
      <Input bind:value={game.group} label={i18n.group[lang]}
        error={errors.find(error => error.fieldName === 'group')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      <div class='admin-games-game-box-group'>
        <!-- VISITOR -->
        <Input bind:value={game.visitor} label={i18n.visitor[lang]} disabled={true}/>

        <!-- HOME -->
        <Input bind:value={game.home} label={i18n.home[lang]} disabled={true}/>
      </div>

      <div class='admin-games-game-box-group'>
        <!-- FIELD -->
        <Input bind:value={game.field} label={i18n.field[lang]} disabled={true}/>

        <!-- PLAY BY PLAY -->
        <Input bind:value={game.playByPlay} label='Play by play' disabled={true}/>
      </div>

      <!-- STREAM -->
      <Input bind:value={game.stream} label='Stream'
        error={errors.find(error => error.fieldName === 'stream')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      <!-- CZECH TELEVISION -->
      <Input bind:value={game.czechTelevision} label={i18n.czechTelevision[lang]}
        error={errors.find(error => error.fieldName === 'czechTelevision')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      <!-- PHOTOGALLERY -->
      <Input bind:value={game.photogallery} label={i18n.photogallery[lang]}
        error={errors.find(error => error.fieldName === 'photogallery')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      <!-- HIGHLIGHTS -->
      <Input bind:value={game.highlights} label='Highlights'
        error={errors.find(error => error.fieldName === 'highlights')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      <div class='admin-games-game-box-group'>
        <!-- SCORE VISITOR -->
        <Input bind:value={game.score.visitor} label={i18n.scoreVisitor[lang]} disabled={true}/>

        <!-- SCORE HOME -->
        <Input bind:value={game.score.home} label={i18n.scoreHome[lang]} disabled={true}/>
      </div>

      <!-- NOTE CS -->
      <Input bind:value={game.note.cs} label='{i18n.note[lang]} [CS]'
        error={errors.find(error => error.fieldName === 'note.cs')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      <!-- NOTE EN -->
      <Input bind:value={game.note.en} label='{i18n.note[lang]} [EN]'
        translate={i18n.automaticallyTranslate[lang]} onTranslateClick={() => { translate('note'); }}
        error={errors.find(error => error.fieldName === 'note.en')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      {#if display === 'loading' || display === 'error'}
        <div class='admin-games-game-box-message-box'>
          <MessageBox value={messageBox ? messageBox[lang] : undefined}/>
        </div>
      {/if}

      {#if display === 'button' || display === 'error'}
        <div class='admin-games-game-box-button'>
          <Button value={i18n.saveChanges[lang]}
            onClick={() => { editGame(); }} icon='save' color='green'/>
        </div>
      {/if}

      {#if display === 'saved'}
        <div class='admin-games-game-box-message-box-and-button'>
          <div class='admin-games-game-box-message-box-and-button-message-box'>
            <MessageBox value={messageBox ? messageBox[lang] : undefined}/>
          </div>

          <div class='admin-games-game-box-message-box-and-button-button'>
            <Button value={i18n.back[lang]} onClick={back} icon='arrow_back'
              cssClass='admin-games-game-box-message-box-and-button-button-component'/>
          </div>
        </div>
      {/if}
    </div>

    <div class='admin-games-game-right-panel'>
      <div class='admin-games-game-right-panel-box'/>

      <div class='admin-games-game-right-panel-save-without-validation'>
        <Button value={i18n.saveChanges[lang] + ' ' + i18n.withoutValidation[lang]}
          onClick={() => { editGame(false); }} icon='warning'/>
      </div>
    </div>
  {/if}
</div>

<Footer bind:lang/>

<style>
  .admin-games-game {
    display: flex;
    justify-content: space-between;
    gap: 40px;

    max-width: 1440px;

    padding: 40px 60px;

    margin: auto;
  }

  .admin-games-game-box {
    display: flex;
    flex-direction: column;
    gap: 40px;

    width: calc((100% - 40px) / 3 * 2);
  }

  .admin-games-game-box-group {
    display: flex;
    gap: 40px;
  }

  .admin-games-game-box-button {
    display: flex;
  }

  .admin-games-game-box-message-box-and-button {
    display: flex;
    gap: 40px;
  }

  .admin-games-game-box-message-box-and-button-message-box {
    width: calc((100% - 40px) / 3 * 2);
  }

  .admin-games-game-box-message-box-and-button-button {
    width: calc((100% - 40px) / 3);
  }

  .admin-games-game-right-panel {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-shrink: 0;
    gap: 40px;

    width: calc((100% - 40px) / 3);
  }

  .admin-games-game-right-panel-box {
    height: 100%;

    padding: 20px;

    border-radius: var(--radius-lg);

    background: var(--grey-1);
  }

  :global(.admin-games-game-box-message-box-and-button-button-component) {
    height: 100% !important;
  }

  :global(body.dark) .admin-games-game-right-panel-box {
    background: var(--grey-8);
  }

  @media only screen and (max-width: 1300px) {
    .admin-games-game-box {
      width: 100%;
    }

    .admin-games-game-right-panel {
      display: none;
    }
  }

  @media only screen and (max-width: 900px) {
    .admin-games-game {
      padding: 40px;
    }

    .admin-games-game-box-message-box-and-button {
      flex-direction: column;
      gap: 20px;
    }

    .admin-games-game-box-message-box-and-button-message-box, .admin-games-game-box-message-box-and-button-button {
      width: 100%;
    }

    :global(.admin-games-game-box-message-box-and-button-button-component) {
      height: 40px !important;
    }
  }

  @media only screen and (max-width: 600px) {
    .admin-games-game-box-group {
      flex-direction: column;
    }
  }

  @media only screen and (max-width: 350px) {
    .admin-games-game {
      padding: 40px 20px;
    }
  }
</style>
